import { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import type { CrmRequest } from '../CrmRequest'
import { intlOf, type Month, type Pills, type Range } from './statisticsData'
import type { StatsStore } from './statsStore'

/** What every Statistics view reads: the cache, the period, the pills and the page's actions. */
export interface StatsCtx {
  store: StatsStore
  request: CrmRequest
  range: Range
  pills: Pills
  setPill: (group: keyof Pills, value: string) => void
  /** "Read more" texts that are open (reset with the period, like the CRM). */
  expanded: ReadonlySet<string>
  toggleExpanded: (id: string) => void
  countriesAll: boolean
  showAllCountries: () => void
  streakMonth: Month
  setStreakMonth: (m: Month) => void
  openChat: (fanId: string) => void
  openWithdrawal: () => void
  /** page.statements write: payout frequency and withdrawals (the server enforces it too). */
  canWritePayouts: boolean
  say: (text: string) => void
  /** When the page opened: the streak calendar's "now" (one cache key per visit). */
  openedAt: Date
}

export const StatsContext = createContext<StatsCtx | null>(null)

export function useStats(): StatsCtx {
  const ctx = useContext(StatsContext)
  if (!ctx) throw new Error('useStats outside the Statistics page')
  return ctx
}

/** The namespace's t, plus the Intl locale for dates. */
export function useStatsT() {
  const { t, i18n } = useTranslation('ofStatistics')
  return { t, intl: intlOf(i18n.language), lang: i18n.language }
}
